import { combine, createStore, restore, sample, useStoreMap } from '@lib/state-engine';
import { IWarehouse, IWarehouseWithBags } from '@@/warehouses/ts';
import {
  loadAllWarehouses,
  loadWarehouseInfo,
  resetWarehouseInfo,
  removeWarehouseById,
  removeWarehouseByIdSuccess,
  removeWarehouseByIdError,
  addWarehouseBag,
  fillWarehouseBag,
  fillWarehouseBagSuccess,
  clearWarehouseBag,
  clearWarehouseBagSuccess,
  clearWarehouseBagError,
  removeWarehouseBag,
  removeWarehouseBagSuccess,
  removeWarehouseBagError,
} from './events';
import {
  fetchRemoveWarehouseBagFx,
  fetchClearWarehouseBagFx,
  fetchRemoveWarehouseByIdFx,
  fetchAllWarehousesFx,
  fetchWarehouseByIdFx,
  fetchAddWarehouseBagFx,
  fetchFillWarehouseBagFx,
} from './effects';
import { WAREHOUSE_TYPES } from './constants';
import {
  WAREHOUSES_ID_QUERY_NAME,
  WAREHOUSES_TYPE_QUERY_NAME,
} from '@@/warehouses/config/query.config';
import { IBag, IBagApi, IBagAdd } from '@@/bags/ts';
import { closeAgricultureModal } from '@@/agriculture/store/agriculture.store';

// TODO: реструктурировать по логическим блокам, которые объеденят все эффекты, события, сторы и т.д.
// TODO: перенести в модуль @/modules/warehouses

export const $isFillingWarehouseBag = fetchFillWarehouseBagFx.pending;
export const $isRemovingBag = fetchRemoveWarehouseBagFx.pending;
export const $isEmptyingBag = fetchClearWarehouseBagFx.pending;
export const $isRemovingWarehouse = fetchRemoveWarehouseByIdFx.pending;

/** Общая информация о всех складах */
export const $allWarehouses = createStore<IWarehouse[]>([]);
export const $isLoadingAllWarehouses = fetchAllWarehousesFx.pending;

/** Информация о выбранном складе */
export const $warehouseInfo = createStore<IWarehouseWithBags | null>(null).reset(
  resetWarehouseInfo,
);
export const $isLoadingWarehouseInfo = fetchWarehouseByIdFx.pending;

/** Список всех бункеров выбранного склада */
export const $warehouseBags = combine($warehouseInfo, (w) => w?.bags ?? []);

export const $warehouseId = combine($warehouseInfo, (w) => w?.id ?? 0);

export const $allSleeveWarehousePositions = combine($allWarehouses, (whs) =>
  whs
    .filter((wh) => wh.type === WAREHOUSE_TYPES.sleeve)
    .map(({ position }) => position),
);

export const useBag = (id: IBagApi['id']) =>
  useStoreMap({
    store: $warehouseBags,
    keys: [id],
    fn: (bags, [id]) => bags.find((b) => b.id === id) ?? GetEmptyBag(),
  });

const $removeBag = restore(removeWarehouseBag, { warehouseId: -1, bagId: '' });
const $emptyBag = restore(clearWarehouseBag, { warehouseId: -1, bagId: '' });

sample({
  clock: loadAllWarehouses,
  target: fetchAllWarehousesFx,
});

sample({
  clock: fetchAllWarehousesFx.doneData,
  target: $allWarehouses,
});

sample({
  clock: loadWarehouseInfo,
  target: fetchWarehouseByIdFx,
});

sample({
  clock: fetchWarehouseByIdFx.doneData,
  target: $warehouseInfo,
});

/* remove bag */

sample({
  clock: removeWarehouseBagSuccess,
  source: { whInfo: $warehouseInfo, removeData: $removeBag },
  fn: ({ whInfo, removeData }) => {
    const info = whInfo as IWarehouseWithBags;
    const bagIdx = info.bags.findIndex((bg) => bg.id === removeData.bagId) ?? -1;
    const newBags = [...info.bags];
    const bag = info.bags[bagIdx];

    const removedBagWeight = bag.weight;
    const removedBagAgroId = bag.agricultureId;

    newBags.splice(bagIdx, 1);

    const newInfo = {
      ...info,
      totalBags: info.totalBags - 1,
      filledBags: info.filledBags - (removedBagAgroId ? 1 : 0),
      totalWeight: info.totalWeight - removedBagWeight,
    };

    return { ...newInfo, bags: newBags };
  },
  target: $warehouseInfo,
});

sample({
  clock: removeWarehouseBagSuccess,
  source: {
    whInfo: $warehouseInfo,
    allWh: $allWarehouses,
    removeData: $removeBag,
  },
  fn: ({ allWh, whInfo, removeData }) => {
    const info = whInfo as IWarehouseWithBags;
    const whIdx = allWh.findIndex((wh) => wh.id === removeData.warehouseId);
    const bagIdx = info.bags.findIndex((bg) => bg.id === removeData.bagId) ?? -1;
    const bag = info.bags[bagIdx];
    const whData = allWh[whIdx];

    const newWh = [...allWh];

    newWh.splice(whIdx, 1, {
      ...whData,
      totalBags: whData.totalBags - 1,
      filledBags: whData.filledBags - (bag.agricultureId ? 1 : 0),
      totalWeight: whData.totalWeight - bag.weight,
    });

    return newWh;
  },
  target: $allWarehouses,
});

sample({
  clock: removeWarehouseBag,
  fn: ({ bagId }) => bagId,
  target: fetchRemoveWarehouseBagFx,
});

sample({
  clock: fetchRemoveWarehouseBagFx.doneData,
  target: removeWarehouseBagSuccess,
});

sample({
  clock: fetchRemoveWarehouseBagFx.failData,
  fn: ({ message }) => message,
  target: removeWarehouseBagError,
});

/* * * */

/* clear bag */

sample({
  clock: clearWarehouseBagSuccess,
  source: { whInfo: $warehouseInfo, emptyData: $emptyBag },
  fn: ({ whInfo, emptyData }) => {
    const info = whInfo as IWarehouseWithBags;
    const bagIdx = info.bags.findIndex((bg) => bg.id === emptyData.bagId) ?? -1;
    const bag = info.bags[bagIdx];
    const newBags = [...info.bags];
    const clearedBagWeight = bag.weight;

    newBags.splice(bagIdx, 1, {
      ...bag,
      agricultureId: null,
      fillingDate: null,
      weight: 0,
    });

    const newWhInfo = {
      ...info,
      filledBags: info.filledBags - 1,
      totalWeight: info.totalWeight - clearedBagWeight,
    };

    return { ...newWhInfo, bags: newBags };
  },
  target: $warehouseInfo,
});

sample({
  clock: clearWarehouseBagSuccess,
  source: { whInfo: $warehouseInfo, allWh: $allWarehouses, emptyData: $emptyBag },
  fn: ({ allWh, whInfo, emptyData }) => {
    const info = whInfo as IWarehouseWithBags;
    const whIdx = allWh.findIndex((wh) => wh.id === emptyData.warehouseId);
    const bagIdx = info.bags.findIndex((bg) => bg.id === emptyData.bagId) ?? -1;
    const bag = info.bags[bagIdx];
    const whData = allWh[whIdx];

    const newWh = [...allWh];

    newWh.splice(whIdx, 1, {
      ...whData,
      filledBags: whData.filledBags - 1,
      totalWeight: whData.totalWeight - bag.weight,
    });

    return newWh;
  },
  target: $allWarehouses,
});

sample({
  clock: clearWarehouseBag,
  source: $warehouseBags,
  fn: (allBags, { bagId }) => {
    const bagIdx = allBags.findIndex((b) => b.id === bagId);
    const bag = allBags[bagIdx];
    return {
      ...bag,
      agricultureId: null,
      fillingDate: null,
      weight: 0,
    };
  },
  target: fetchClearWarehouseBagFx,
});

sample({
  clock: fetchClearWarehouseBagFx.doneData,
  target: clearWarehouseBagSuccess,
});

sample({
  clock: fetchClearWarehouseBagFx.failData,
  fn: ({ message }) => message,
  target: clearWarehouseBagError,
});

/* * * */

/* add bag */

sample({
  clock: addWarehouseBag,
  fn: (whId) =>
    ({
      warehouse_id: whId,
      agriculture_id: null,
      filling_date: null,
      position: null,
      values: [],
      weight: 0,
    } as IBagAdd),
  target: fetchAddWarehouseBagFx,
});

sample({
  clock: fetchAddWarehouseBagFx.doneData,
  source: $warehouseId,
  target: [loadWarehouseInfo, loadAllWarehouses],
});

/* * * */

/* fill bag */

sample({
  clock: fillWarehouseBag,
  target: fetchFillWarehouseBagFx,
});

sample({
  clock: fetchFillWarehouseBagFx.doneData,
  target: fillWarehouseBagSuccess,
});

sample({
  clock: fillWarehouseBagSuccess,
  source: $warehouseInfo,
  filter: (whInfo) =>
    Boolean(whInfo) &&
    (whInfo!.type === WAREHOUSE_TYPES.sleeve ||
      (whInfo!.type === WAREHOUSE_TYPES.stationary &&
        whInfo!.totalBags - whInfo!.filledBags > 1)),
  fn: (whInfo) => whInfo!.id,
  target: [loadWarehouseInfo, loadAllWarehouses, closeAgricultureModal],
});

sample({
  clock: fillWarehouseBagSuccess,
  source: $warehouseInfo,
  filter: (whInfo) => {
    return (
      Boolean(whInfo) &&
      whInfo!.type === WAREHOUSE_TYPES.stationary &&
      whInfo!.totalBags - whInfo!.filledBags === 1
    );
  },
  fn: (whInfo) => {
    return whInfo!.id;
  },
  target: addWarehouseBag,
});

/* * * */

/* remove warehouse */

sample({
  clock: removeWarehouseById,
  target: fetchRemoveWarehouseByIdFx,
});

sample({
  clock: fetchRemoveWarehouseByIdFx.doneData,
  target: [removeWarehouseByIdSuccess, loadAllWarehouses],
});

sample({
  clock: fetchRemoveWarehouseByIdFx.failData,
  target: removeWarehouseByIdError,
});

sample({
  clock: removeWarehouseByIdSuccess,
  fn: () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(WAREHOUSES_ID_QUERY_NAME);
    params.delete(WAREHOUSES_TYPE_QUERY_NAME);

    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`,
    );
  },
});

function GetEmptyBag(): IBag {
  return {
    id: '',
    weight: 0,
    values: [],
    warehouseId: -1,
    fillingDate: null,
    agricultureId: null,
    position: [],
  };
}
